import React, { useEffect, useState } from 'react';
import { XIcon, PackageIcon, SaveIcon, CheckIcon, LayersIcon } from 'lucide-react';
import { MOCK_IMAGE_SETS } from '../schemas/mockData';
import type { Superset, AgeBand } from '../schemas/types';
interface SupersetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (superset: Omit<Superset, 'id' | 'createdAt' | 'updatedAt' | 'version'>) => void;
  editingSuperset?: Superset | null;
}
export function SupersetModal({
  isOpen,
  onClose,
  onSave,
  editingSuperset
}: SupersetModalProps) {
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    status: 'draft' | 'published';
    ageBands: AgeBand[];
    locales: string[];
    setIds: string[];
    policies: {
      avoidRepeatsPerParticipant: boolean;
      repeatWindowSessions: number;
      fillStrategy: 'random' | 'sequential' | 'balanced';
    };
  }>(editingSuperset || {
    name: '',
    description: '',
    status: 'draft',
    ageBands: [],
    locales: ['en'],
    setIds: [],
    policies: {
      avoidRepeatsPerParticipant: true,
      repeatWindowSessions: 3,
      fillStrategy: 'balanced'
    }
  });
  const [selectedSets, setSelectedSets] = useState<Set<string>>(new Set(editingSuperset?.setIds || []));
  // Update form data when editingSuperset changes
  useEffect(() => {
    if (editingSuperset) {
      setFormData(editingSuperset);
      setSelectedSets(new Set(editingSuperset.setIds));
    }
  }, [editingSuperset]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      setIds: Array.from(selectedSets)
    });
  };
  const toggleSetSelection = (setId: string) => {
    const newSelected = new Set(selectedSets);
    if (newSelected.has(setId)) {
      newSelected.delete(setId);
    } else {
      newSelected.add(setId);
    }
    setSelectedSets(newSelected);
  };
  const toggleAgeBand = (band: AgeBand) => {
    setFormData(prev => ({
      ...prev,
      ageBands: prev.ageBands.includes(band) ? prev.ageBands.filter(b => b !== band) : [...prev.ageBands, band]
    }));
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto" data-id="element-425">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full my-8" data-id="element-426">
        <form onSubmit={handleSubmit} data-id="element-427">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10 rounded-t-xl" data-id="element-428">
            <div className="flex items-center gap-3" data-id="element-429">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center" data-id="element-430">
                <PackageIcon className="w-6 h-6 text-indigo-600" data-id="element-431" />
              </div>
              <div data-id="element-432">
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-433">
                  {editingSuperset ? 'Edit Superset' : 'Create Superset'}
                </h2>
                <p className="text-sm text-gray-600 mt-0.5" data-id="element-434">
                  Combine multiple image sets with advanced policies
                </p>
              </div>
            </div>
            <button type="button" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-435">
              <XIcon className="w-6 h-6 text-gray-600" data-id="element-436" />
            </button>
          </div>
          {/* Form Content */}
          <div className="p-6 space-y-6 max-h-[calc(90vh-200px)] overflow-y-auto" data-id="element-437">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-6" data-id="element-438">
              <div data-id="element-439">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-440">
                  Name *
                </label>
                <input type="text" value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} required className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="e.g., Complete Assessment Superset" data-id="element-441" />
              </div>
              <div data-id="element-442">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-443">
                  Status
                </label>
                <select value={formData.status} onChange={e => setFormData({
                ...formData,
                status: e.target.value as 'draft' | 'published'
              })} className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-444">
                  <option value="draft" data-id="element-445">Draft</option>
                  <option value="published" data-id="element-446">Published</option>
                </select>
              </div>
            </div>
            <div data-id="element-447">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-448">
                Description
              </label>
              <textarea value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} rows={3} className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="Describe this superset..." data-id="element-449" />
            </div>
            {/* Age Bands */}
            <div data-id="element-450">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-451">
                Age Bands
              </label>
              <div className="flex flex-wrap gap-2" data-id="element-452">
                {(['5-9', '10-14', '14-18', 'Adults', 'Students'] as AgeBand[]).map(band => <button key={band} type="button" onClick={() => toggleAgeBand(band)} className={`px-4 py-2 rounded-lg border-2 font-semibold transition-all ${formData.ageBands.includes(band) ? 'bg-indigo-100 border-indigo-500 text-indigo-700' : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'}`} data-id="element-453">
                    {band}
                  </button>)}
              </div>
            </div>
            {/* Policies */}
            <div className="bg-indigo-50 border-2 border-indigo-200 rounded-lg p-4" data-id="element-454">
              <h3 className="text-sm font-bold text-indigo-900 mb-4" data-id="element-455">
                Superset Policies
              </h3>
              <div className="space-y-4" data-id="element-456">
                <div className="flex items-center justify-between" data-id="element-457">
                  <div data-id="element-458">
                    <label className="text-sm font-semibold text-indigo-900" data-id="element-459">
                      Avoid Repeats Per Participant
                    </label>
                    <p className="text-xs text-indigo-700 mt-1" data-id="element-460">
                      Prevent showing the same images to participants within a
                      session window
                    </p>
                  </div>
                  <input type="checkbox" checked={formData.policies.avoidRepeatsPerParticipant} onChange={e => setFormData({
                  ...formData,
                  policies: {
                    ...formData.policies,
                    avoidRepeatsPerParticipant: e.target.checked
                  }
                })} className="w-5 h-5 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500" data-id="element-461" />
                </div>
                {formData.policies.avoidRepeatsPerParticipant && <div data-id="element-462">
                    <label className="block text-sm font-semibold text-indigo-900 mb-2" data-id="element-463">
                      Repeat Window (sessions)
                    </label>
                    <input type="number" value={formData.policies.repeatWindowSessions} onChange={e => setFormData({
                  ...formData,
                  policies: {
                    ...formData.policies,
                    repeatWindowSessions: parseInt(e.target.value) || 3
                  }
                })} min={1} className="w-32 px-4 py-2 border-2 border-indigo-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-464" />
                  </div>}
                <div data-id="element-465">
                  <label className="block text-sm font-semibold text-indigo-900 mb-2" data-id="element-466">
                    Fill Strategy
                  </label>
                  <select value={formData.policies.fillStrategy} onChange={e => setFormData({
                  ...formData,
                  policies: {
                    ...formData.policies,
                    fillStrategy: e.target.value as 'random' | 'sequential' | 'balanced'
                  }
                })} className="w-full px-4 py-2 border-2 border-indigo-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-467">
                    <option value="random" data-id="element-468">Random</option>
                    <option value="sequential" data-id="element-469">Sequential</option>
                    <option value="balanced" data-id="element-470">Balanced</option>
                  </select>
                </div>
              </div>
            </div>
            {/* Image Set Selection */}
            <div data-id="element-471">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-472">
                Select Image Sets ({selectedSets.size} selected)
              </label>
              <div className="grid grid-cols-1 gap-3 max-h-96 overflow-y-auto p-4 bg-gray-50 rounded-lg border-2 border-gray-200" data-id="element-473">
                {MOCK_IMAGE_SETS.map(set => <div key={set.id} onClick={() => toggleSetSelection(set.id)} className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${selectedSets.has(set.id) ? 'border-indigo-500 bg-indigo-50 shadow-sm' : 'border-gray-200 bg-white hover:border-indigo-300'}`} data-id="element-474">
                    <div className="flex items-start justify-between" data-id="element-475">
                      <div className="flex-1" data-id="element-476">
                        <div className="flex items-center gap-2 mb-1" data-id="element-477">
                          <h4 className="text-sm font-bold text-gray-900" data-id="element-478">
                            {set.name}
                          </h4>
                          <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${set.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-479">
                            {set.status}
                          </span>
                        </div>
                        {set.description && <p className="text-xs text-gray-600 mb-2" data-id="element-480">
                            {set.description}
                          </p>}
                        <div className="flex items-center gap-3 text-xs text-gray-500" data-id="element-481">
                          <div className="flex items-center gap-1" data-id="element-482">
                            <LayersIcon className="w-3 h-3" data-id="element-483" />
                            <span data-id="element-484">{set.images.length} images</span>
                          </div>
                          <span data-id="element-485">{set.ageBands.join(', ')}</span>
                        </div>
                      </div>
                      {selectedSets.has(set.id) && <div className="w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center flex-shrink-0 ml-3" data-id="element-486">
                          <CheckIcon className="w-4 h-4 text-white" data-id="element-487" />
                        </div>}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
          {/* Footer */}
          <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3 sticky bottom-0 bg-white rounded-b-xl" data-id="element-488">
            <button type="button" onClick={onClose} className="px-6 py-2.5 border-2 border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold" data-id="element-489">
              Cancel
            </button>
            <button type="submit" disabled={!formData.name || selectedSets.size === 0} className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-lg hover:from-indigo-700 hover:to-indigo-800 transition-all font-semibold shadow-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-490">
              <SaveIcon className="w-5 h-5" data-id="element-491" />
              {editingSuperset ? 'Save Changes' : 'Create Superset'}
            </button>
          </div>
        </form>
      </div>
    </div>;
}